"use client";

import Link from "next/link";
import React, { useState } from "react";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline";
import TelegramListingChannelsFollow from "@/components/telegram/TelegramListingChannelsFollow";

export default function FAQ() {
  const [expandedCategories, setExpandedCategories] = useState<number[]>([0]); // Start with first category expanded

  const toggleCategory = (categoryIndex: number) => {
    setExpandedCategories((prev) =>
      prev.includes(categoryIndex)
        ? prev.filter((index) => index !== categoryIndex)
        : [...prev, categoryIndex],
    );
  };

  const faqs = [
    // About Sell It Off
    {
      category: "About Sell It Off",
      items: [
        {
          question: "What is Sell It Off?",
          answer:
            "Sell It Off is a platform designed for buying and selling pre-owned household items starting with Lagos, Nigeria. We connect sellers who want to declutter with buyers looking for quality used items at great prices. Our platform makes the process safe, transparent, and convenient for all parties.",
        },
        {
          question: "How does Sell It Off work?",
          answer:
            "Sellers list their pre-owned items with descriptions, photos, and pickup details. Our team reviews each listing for quality assurance. Buyers find items near them, make secure payments through our platform, and arrange pickup with the seller. Once the item is picked up and confirmed, the seller receives payment.",
        },
        {
          question: "What areas do you serve?",
          answer:
            "We currently serve Lagos, Nigeria, with plans to expand to other cities in Nigeria and Africa. Our platform connects buyers and sellers across all neighborhoods in Lagos, including Ikeja, Lekki, Victoria Island, Ikoyi, and many more.",
        },
        {
          question: "What makes Sell It Off different from other platforms?",
          answer:
            "Sell It Off specializes in household items with verified listings, secure payment processing, and a focus on location-based matching to reduce logistics challenges. We also have strict quality standards, a user-friendly interface, and a dedicated customer support team. Lastly, Sell It Off has a lot of features in store that will make it easier to sell and buy items - we are always working to improve the platform for you. Stay tuned!",
        },
      ],
    },

    // Understanding Sell It Off
    {
      category: "Understanding Sell It Off",
      items: [
        {
          question: "Why does Sell It Off only accept certain item categories?",
          answer: (
            <>
              We focus on{" "}
              <span className="font-bold">neatly used household items</span>{" "}
              because they're the safest, easiest category to verify,
              photograph, and inspect at pickup. Categories like gadgets,
              fashion, and vehicles tend to have higher fraud risk, more
              disputes, or require specialized verification we can't yet
              guarantee. Limiting our scope is how we keep trust high for both
              buyers and sellers. See our{" "}
              <Link href="/terms" className="text-blue-500">
                Terms of Service
              </Link>{" "}
              for the current accepted and prohibited item list.
            </>
          ),
        },
        {
          question: "How does Sell It Off protect both buyers and sellers?",
          answer: (
            <>
              Every transaction follows the same flow:
              <ol className="list-decimal list-inside mt-2">
                <li>
                  The buyer pays through Sell It Off (not directly to the
                  seller).
                </li>
                <li>
                  The buyer receives the seller's contact details and arranges
                  pickup within 48 hours.
                </li>
                <li>
                  The buyer inspects the item in person before confirming
                  receipt.
                </li>
                <li>
                  Only after the buyer confirms pickup is the payment released
                  to the seller (minus our 10% fee).
                </li>
              </ol>
              <p className="mt-3">
                This means a buyer is never out of pocket for an item they
                haven't seen, and a seller never hands over an item without
                being sure they'll get paid.
              </p>
            </>
          ),
        },
        {
          question: "Why doesn't Sell It Off support payment on delivery?",
          answer:
            "Payment on delivery puts both sides at risk: sellers can be cancelled on at the last minute after travelling out, and buyers have no record of the transaction if something goes wrong. By processing payments upfront and holding them until pickup is confirmed, we make sure both parties show up serious and protected.",
        },
        {
          question: "Why is pickup required within 48 hours of payment?",
          answer:
            "The 48-hour window keeps transactions moving and respects the seller's time — they're holding the item for you and not selling it to anyone else during that period. If you delay pickup beyond 48 hours from receiving seller details, or fail to stay in prompt contact, the reserved sale may be released and the item unlocked for other buyers. If you are then due a refund, a 5% administrative fee is deducted from your payment before the remainder is returned to you. See our full Refund Policy for details.",
        },
        {
          question:
            "Why can I only lower the price after approval, not raise it?",
          answer:
            "Buyers see your listing at its approved price, and raising it after the fact would be unfair to anyone considering the item. Lowering the price is always allowed because it benefits buyers. If you genuinely need a higher price, you'd need to create a new listing and go through approval again.",
        },
        {
          question: "Why are inspections so strongly encouraged at pickup?",
          answer:
            "Once you confirm receipt and the seller is paid, no refund will be issued. This is the single most important moment in the transaction. Always physically inspect the item at the pickup location — test that it works, check for damage, and compare it to the listing photos and description. If anything is off, you can request a full refund before confirming.",
        },
        {
          question: "What is Sell It Off actually responsible for?",
          answer:
            "We're responsible for the platform itself, payment processing and escrow, listing quality review, and support during disputes. We are not responsible for delivery (pickup is buyer-arranged), and we are not liable for defects or issues identified after pickup has been confirmed. Think of us as the safe meeting point — the inspection and final decision are yours.",
        },
      ],
    },

    // Account and Registration
    {
      category: "Account and Registration",
      items: [
        {
          question: "How do I create an account?",
          answer:
            "Creating an account is easy! On mobile, click on the 3 lines menu button in the top right corner (on desktop, click on the 'Sign Up' button in the top right corner), click on 'Sign Up' and fill in your details. Check your email for verification. Once verified, you can start buying and selling immediately.",
        },
        {
          question: "Is Sell It Off free to use?",
          answer:
            "Yes, creating an account, listing items for sale, and browsing listings on Sell It Off is completely free. We only charge a 10% fee when your item is sold. We only win when you win, which ensures we're committed to your success.",
        },
        {
          question: "How do I update my profile information?",
          answer:
            "To update your profile, log in to your account and click on your profile icon on the menu bar (on desktop, click on the down arrow icon in the top right corner, select 'Your Profile' from the dropdown menu). From there, you can update your personal information, contact details, and location.",
        },
        // {
        // question: "Can I delete my account?",
        // answer: "Yes, you can delete your account. Navigate to your profile settings and select the 'Delete Account' option. Please note that deleting your account will remove all your information, listings and transaction history."
        // }
      ],
    },

    // Selling Items
    {
      category: "Selling Items",
      items: [
        {
          question: "How do I list an item for sale?",
          answer:
            "To list an item, on mobile, click on the 'Sell' button on the menu bar. Fill in the item details including title, description, price, condition, and photos. Make sure to provide accurate information and clear images to attract potential buyers. On desktop, click on the 'Sell' button on the top nav bar and follow the same process. Once submitted, our admin team will review your listing within 24 hours.",
        },
        {
          question: "Can I list several items at once?",
          answer:
            "Yes. On the sell page, choose Several items (2–10) at the top. You enter location and pickup details once, then add each item's photos and details. When everything looks good, review and submit the whole batch together.",
        },
        {
          question: "What can I sell on Sell It Off?",
          answer: (
            <>
              You can sell neatly used household items across approved
              categories. We keep accepted categories and restrictions updated
              in real-time, so check our{" "}
              <Link href="/terms" className="text-blue-500">
                Terms of Service
              </Link>{" "}
              for the latest accepted and prohibited item policies.
            </>
          ),
        },
        {
          question: "What items are not accepted on Sell It Off?",
          answer: (
            <>
              We maintain a baseline prohibited-items policy (for example
              illegal goods, hazardous materials, counterfeit items, and other
              restricted items), and we also add any currently deactivated
              categories. Please review our{" "}
              <Link href="/terms" className="text-blue-500">
                Terms of Service
              </Link>{" "}
              for the latest prohibited-item list.
            </>
          ),
        },
        {
          question: "How many pictures can I upload?",
          answer:
            "You need to upload exactly 3 images for each item listing. This ensures buyers get a comprehensive view of the item while keeping the listing process streamlined. Make sure to include clear photos from different angles that accurately represent the item's condition.",
        },
        {
          question: "How do I set the right price for my item?",
          answer:
            "We recommend pricing your item between 30-50% of its original value, depending on its condition, age, and demand. Research similar items on our platform to gauge the market. Remember, lower prices typically result in faster sales. All items must be priced between ₦20,000 and ₦3,000,000.",
        },
        {
          question: "Can I change the price after my listing is approved?",
          answer:
            "Once your item is approved, price adjustments can only be made downwards. This policy helps maintain fair pricing and buyer trust in our platform. Also note that, once an item's detail is edited, it has to be re-approved and there's a 24 hour grace period before the details can be changed again.",
        },
        {
          question: "How long does it take for my listing to be approved?",
          answer: (
            <>
              Our admin team typically reviews and approves listings within 24
              hours. If your listing hasn't been approved within this timeframe,
              please check if it meets our quality standards or{" "}
              <Link href="/contact-us" className="text-blue-500">
                contact our support team
              </Link>{" "}
              for assistance.
            </>
          ),
        },
        {
          question: "When do I receive payment for my sold item?",
          answer:
            "You will receive payment once the buyer picks up the item from you and you both confirm receipt (this will be available on the app soon). The payment will be processed immediately after confirmation, minus our 10% service fee.",
        },
        {
          question: "How do I promote my listings?",
          answer:
            "At Sell It Off, every approved listing is automatically optimized for visibility to the right buyers. You don't need to pay for promotions — just ensure that you follow the instructions on the item listing page to create a good listing: a proper title, clear photos, accurate description, location and a fair price to attract more interest from buyers.",
        },
      ],
    },

    // Creating Quality Listings That Get Approved
    {
      category: "Creating Quality Listings That Get Approved",
      items: [
        {
          question: "What does Sell It Off look for when approving a listing?",
          answer:
            "Our admin team reviews every listing for three things: (1) the item belongs to an accepted household category, (2) the photos clearly show the actual item and its real condition, and (3) the title, description, price, and location are accurate and consistent with the photos. Listings that meet these standards are usually approved within 24 hours.",
        },
        {
          question: "What kinds of items get approved most easily?",
          answer:
            "Neatly used household items in good working condition — for example: furniture, kitchenware, small appliances (non-faulty), home décor, baby gear, and similar everyday household goods. If the item looks clean, functions properly, and clearly belongs in a home, it has a strong chance of approval.",
        },
        {
          question: "What gets a listing rejected?",
          answer: (
            <>
              The most common reasons are:
              <ul className="list-disc list-inside mt-2">
                <li>
                  The item falls under a prohibited category (gadgets, fashion
                  items, vehicles, faulty electronics, perishables/food). See
                  our{" "}
                  <Link href="/terms" className="text-blue-500">
                    Terms of Service
                  </Link>{" "}
                  for the full prohibited list.
                </li>
                <li>
                  Photos are blurry, dark, stock images, screenshots from other
                  sites, or don't clearly show the item.
                </li>
                <li>
                  The description is too short, vague, or doesn't match the
                  photos.
                </li>
                <li>
                  The price is unrealistic for the item's condition (too high,
                  or unusually low in a way that looks suspicious).
                </li>
                <li>Missing or inaccurate pickup location.</li>
              </ul>
            </>
          ),
        },
        {
          question: "How should I take photos for my listing?",
          answer: (
            <>
              You need exactly 3 photos per listing. To give your item the best
              chance of approval and a faster sale:
              <ul className="list-disc list-inside mt-2">
                <li>Use good lighting — natural daylight works best.</li>
                <li>
                  Take photos of the actual item you're selling (no stock images
                  or photos from the internet).
                </li>
                <li>
                  Show the item from different angles (e.g. front, side,
                  close-up of any wear or detail).
                </li>
                <li>
                  If the item has any minor flaws, include a clear photo of them
                  — honesty builds buyer trust and prevents refund disputes at
                  pickup.
                </li>
              </ul>
            </>
          ),
        },
        {
          question: "How should I write the description?",
          answer: (
            <>
              Be specific and honest. A good description includes:
              <ul className="list-disc list-inside mt-2">
                <li>
                  What the item is (brand, model, size, color if relevant).
                </li>
                <li>How old it is and how often it was used.</li>
                <li>
                  Its current condition, including any wear, scratches, or
                  missing parts.
                </li>
                <li>
                  Whether it comes with accessories, original packaging, or a
                  manual.
                </li>
                <li>
                  Any reason a buyer might want it (e.g. "barely used,
                  originally bought for ₦X").
                </li>
              </ul>
              <p className="mt-3">
                Avoid one-line descriptions like "good condition, DM me" — these
                often get rejected and rarely sell.
              </p>
            </>
          ),
        },
        {
          question: "How should I price my item?",
          answer:
            "We generally recommend pricing between 30–50% of the original retail value, depending on age, condition, and demand. All items must be priced between ₦20,000 and ₦3,000,000. Remember: once your listing is approved, you can only adjust the price downwards, so price fairly from the start. Browsing similar items already on the platform is a good way to gauge the market.",
        },
        {
          question: "My listing was rejected — what do I do?",
          answer: (
            <>
              Review the rejection reason carefully, fix the issue (better
              photos, clearer description, correct category, more realistic
              price), and resubmit. If you're unsure why your listing wasn't
              approved or you believe it was rejected in error,{" "}
              <Link href="/contact-us" className="text-blue-500">
                contact our support team
              </Link>{" "}
              and we'll guide you.
            </>
          ),
        },
        {
          question: "Can I edit my listing after approval?",
          answer:
            "Yes. Any edit sends the listing back into review for re-approval. There's also a 24-hour grace period after an edit before the listing details can be edited again — this prevents constant back-and-forth changes that confuse buyers.",
        },
        {
          question: "How do I get more buyers to see my listing?",
          answer:
            "You don't need to pay for promotions — Sell It Off automatically surfaces approved listings to nearby buyers and to buyers searching for relevant items. The best way to attract more interest is by following the quality guidelines above: clear title, honest description, sharp photos, fair price, and accurate location.",
        },
      ],
    },

    // Buying Items
    {
      category: "Buying Items",
      items: [
        {
          question: "How do I find items near me?",
          answer:
            "Our platform automatically shows you items based on your location. You can also use the search bar and filters to find specific items by category, location, or price range. The distance from your location is displayed on each listing to help you make informed decisions.",
        },
        {
          question: "How do I enable browser push notifications?",
          answer: (
            <span id="notifications-browser">
              On desktop Chrome/Edge/Firefox, allow notifications when prompted
              or in site settings. On iPhone, add Sell It Off to your Home
              Screen (PWA), open the app from the icon, then enable push under
              Profile → Notifications → Browser push devices. If you previously
              blocked notifications, open your browser or device settings, find
              Sell It Off, and allow alerts for this site.
            </span>
          ),
        },
        {
          question: "How do near-me alerts work?",
          answer: (
            <>
              Sell It Off can notify you when new listings are approved near
              your profile location. In-app alerts (and optional browser push)
              are always available. You can also turn on email or WhatsApp under{" "}
              <Link href="/profile?tab=notifications" className="text-blue-500">
                Profile → Notifications
              </Link>
              . WhatsApp is optional and requires a valid phone number on your
              profile; when enabled, near-me WhatsApp alerts are sent in real
              time. For a public browse-anytime feed, follow the Lagos or Oyo{" "}
              <Link
                href="/faq#telegram-listing-channels"
                className="text-blue-500"
              >
                Telegram listing channels
              </Link>
              . Community WhatsApp groups are separate chat rooms — not tailored
              to your location and not the same as near-me alerts or Telegram.
            </>
          ),
        },
        {
          question: "How do I follow listings on Telegram?",
          answer: (
            <span id="telegram-listing-channels" className="block space-y-3">
              <span className="block">
                Sell It Off posts approved Lagos and Oyo listings to public
                Telegram channels. Tap Join / Follow on the channel for your
                area. Telegram is a public feed you browse — it does not replace
                near-me alerts, which message you about items near your profile
                location.
              </span>
              <TelegramListingChannelsFollow
                showHeading={false}
                description=""
              />
            </span>
          ),
        },
        {
          question: "How do I buy an item on Sell It Off?",
          answer:
            "Browse and search for the item you need on our platform. Carefully review item details, including location and proximity, before making payment. Once payment is made, you'll receive pickup instructions and seller contact information. Please note that we do not support 'payment on delivery'.",
        },
        {
          question: "How do payments work?",
          answer:
            "We use secure payment processing to protect both buyers and sellers. Buyers pay through our platform, and funds are held in escrow until the item is received and approved. Sellers get paid immediately after pickup is confirmed.",
        },
        {
          question: "What happens after I make a payment?",
          answer:
            "After making a payment, you'll receive the seller's contact information and must schedule inspection and pickup within 48 hours. You're strongly encouraged to personally inspect items before confirming receipt. Once you've confirmed pickup, payment is released to the seller.",
        },
        {
          question: "Is delivery available?",
          answer:
            "Presently, delivery is not available on Sell It Off. Sellers set their preferred pickup locations and availability. If you need delivery, you can discuss this directly with the seller after establishing contact, but note that Sell It Off does not handle or provide delivery services at the moment. We will let you know if delivery becomes available.",
        },
        {
          question: "What if an item isn't as described?",
          answer:
            "If you discover at the pickup location that an item doesn't match its advertised description, you can request a full refund immediately before confirming pickup. However, once you've picked up the item and confirmed receipt, no refund will be issued. Always thoroughly inspect items before confirmation.",
        },
        {
          question: "Can I return an item after purchase?",
          answer:
            "Once an item has been picked up and marked as delivered, and the seller has been paid, no refund will be issued. Sell It Off is not liable for any defects or issues identified after pickup. We strongly advise buyers to thoroughly inspect and test items before confirming receipt.",
        },
      ],
    },

    // Refund Policy
    {
      category: "Refund Policy",
      items: [
        {
          question: "What is Sell It Off's refund policy?",
          answer: (
            <>
              At Sell It Off, we prioritize transparency and buyer satisfaction.
              Our refund policy is designed to ensure fair transactions while
              protecting both buyers and sellers. Read the full policy on our{" "}
              <Link href="/refund-policy" className="text-blue-500">
                Refund Policy
              </Link>{" "}
              page.
            </>
          ),
        },
        {
          question: "When am I eligible for a full refund?",
          answer: (
            <>
              Full refunds (100%) are only applicable under specific
              circumstances at the pickup location:
              <ul className="list-disc list-inside">
                <li>
                  There is a clear mismatch between the advertised description
                  and the actual condition of the item
                </li>
                <li>
                  The item is no longer available or the seller changes their
                  mind (except when due to buyer delays)
                </li>
                <li>
                  The item has a significant defect that was not previously
                  disclosed in the listing
                </li>
              </ul>
            </>
          ),
        },
        {
          question: "Are there any restrictions on refunds?",
          answer: (
            <>
              Yes:
              <ul className="list-disc list-inside">
                <li>
                  Once an item has been picked up and marked as delivered, and
                  the seller has been paid, no refund will be issued.
                </li>
                <li>
                  If you do not schedule pickup or communicate promptly within
                  48 hours of receiving seller details, the reserved sale may be
                  released and the item unlocked for other buyers. If a refund
                  is then due to you, a 5% administrative fee will be deducted
                  from your payment before the remainder is refunded.
                </li>
              </ul>
            </>
          ),
        },
        {
          question: "How do I request a refund?",
          answer: (
            <>
              If you need to request a refund, do so immediately at the pickup
              location if the item doesn&apos;t match its description or has
              undisclosed defects — do not confirm receipt — then{" "}
              <Link href="/contact-us" className="text-blue-500">
                contact our support team
              </Link>
              . See the{" "}
              <Link href="/refund-policy" className="text-blue-500">
                Refund Policy
              </Link>{" "}
              for eligibility and fees. Soon, you will be able to request a
              refund through the platform.
            </>
          ),
        },
      ],
    },

    // Pickup and Logistics
    {
      category: "Pickup and Logistics",
      items: [
        {
          question: "How do I arrange pickup for an item?",
          answer: (
            <>
              After purchasing an item, you'll receive the seller's contact
              information. Use this to communicate directly with the seller and
              arrange a pickup time based on their availability schedule.{" "}
              <span className="font-bold">
                You must schedule inspection and pickup within 48 hours of
                receiving seller details.
              </span>{" "}
              Always ensure the pickup location is safe and comfortable for both
              parties.
            </>
          ),
        },
        {
          question: "How do I ship items?",
          answer:
            "Currently, we do not handle logistics. After a sale, buyers are responsible for arranging transportation of the item directly with the seller after pickup is confirmed. Buyers can decide their preferred mode of transportation.",
        },
        {
          question: "What if I can't meet during the seller's available times?",
          answer: (
            <>
              If you cannot meet during the seller's specified availability
              times, negotiate an alternative date and time{" "}
              <span className="font-bold">and inform us</span>. Most sellers are
              flexible, but please be respectful of their schedules.
            </>
          ),
        },
        {
          question: "Is Sell It Off responsible for delivery issues?",
          answer:
            "Presently, Sell It Off does not handle or provide delivery services. Buyers and sellers are responsible for arranging their own pickup and delivery logistics. We are not responsible for any logistics costs or issues that arise during pickup or delivery.",
        },
      ],
    },

    // Payments and Fees
    {
      category: "Payments and Fees",
      items: [
        {
          question: "What payment methods are accepted?",
          answer:
            "We accept various payment methods including credit/debit cards and bank transfers. All payments are processed securely through our platform to ensure protection for both buyers and sellers.",
        },
        {
          question: "How much does Sell It Off charge?",
          answer:
            "Sell It Off charges a 10% commission fee on successful sales. This fee is automatically deducted from the seller's payment when a transaction is completed. There are no listing fees or other hidden charges.",
        },
        {
          question: "When do sellers receive payment?",
          answer:
            "Sellers receive payment immediately after the buyer confirms pickup and receipt of the item. The funds are transferred to the seller's account after deducting our 10% service fee.",
        },
        {
          question: "What if I need to cancel an order?",
          answer:
            "Once payment is made and seller's information is released to you, cancellations are limited. Buyers are advised to arrange pickup within 24 to 48 hours. If you do need to cancel, please contact support immediately — cancellations may be subject to review and may attract a cancellation fee.",
        },
      ],
    },

    // Safety and Security
    {
      category: "Safety and Security",
      items: [
        {
          question: "How does Sell It Off ensure safety?",
          answer:
            "While we try our best to ensure some level of safety, we seriously advise buyers and sellers to be cautious and meet in public places for transactions. That said, currently, we implement several safety measures including minimum user verification, secure payment processing, and item quality reviews.",
        },
        {
          question: "What happens if I receive a damaged item?",
          answer:
            "At Sell It Off, we release the payment to the seller immediately after you confirm pickup. Because of this, we cannot offer refunds after confirmation. We strongly advise buyers to thoroughly inspect and test items before confirming receipt. If you encounter an issue before confirmation, please report it immediately.",
        },
        {
          question: "How do I report suspicious activity?",
          answer: (
            <>
              If you encounter suspicious activity or users, please{" "}
              <Link href="/contact-us" className="text-blue-500">
                contact our support team
              </Link>{" "}
              immediately. We take all reports seriously and investigate them
              promptly. Soon you'll be able to report suspicious activity
              through the platform.
            </>
          ),
        },
        {
          question: "Are my personal details shared with other users?",
          answer:
            "We only share necessary contact information between buyers and sellers after a purchase is confirmed. Your full profile details (if shared with Sell It Off), payment information, and other sensitive data are never shared with other users.",
        },
      ],
    },

    // Prohibited Items
    {
      category: "Prohibited Items",
      items: [
        {
          question: "What items are prohibited?",
          answer: (
            <>
              Prohibited items include fashion items (clothing, shoes, bags,
              hair products, makeup/accessories), vehicles (cars, motorcycles,
              tricycles/keke), faulty electronics, perishable items or food,
              illegal goods, counterfeit items, hazardous materials, items that
              violate intellectual property rights, live animals, and digital
              products with no physical component. Check our{" "}
              <Link href="/terms" className="text-blue-500">
                Terms of Service
              </Link>{" "}
              for a complete list.
            </>
          ),
        },
      ],
    },

    // Support
    {
      category: "Support",
      items: [
        {
          question: "How can I contact support?",
          answer: (
            <>
              You can reach our support team through the{" "}
              <Link href="/contact-us" className="text-blue-500">
                Contact Us
              </Link>{" "}
              page, by email at support@sellitoffnow.com, or via WhatsApp during
              business hours. Your feedback is important to us, and we strive to
              respond as fast as possible. Soon you'll be able to contact our
              support team through the platform.
            </>
          ),
        },
        {
          question: "What do I do if I have a dispute with another user?",
          answer:
            "If you have a dispute with another user, first try to resolve it through direct communication. If that doesn't work, contact our support team who can mediate and help find a fair resolution for both parties.",
        },
      ],
    },
  ];

  return (
    <div className="max-w-4xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
      <h1 className="text-4xl font-bold text-foreground mb-4">
        Frequently Asked Questions
      </h1>
      <p className="text-text-light mb-8">
        Find answers to common questions about using Sell It Off
      </p>

      <div className="space-y-6">
        {faqs.map((category, categoryIndex) => (
          <div
            key={categoryIndex}
            className="border rounded-lg overflow-hidden"
          >
            <button
              onClick={() => toggleCategory(categoryIndex)}
              className="w-full flex justify-between items-center p-4 bg-secondary hover:bg-tertiary transition-colors text-left"
            >
              <h2 className="text-xl font-semibold text-foreground">
                {category.category}
              </h2>
              {expandedCategories.includes(categoryIndex) ? (
                <ChevronUpIcon className="h-6 w-6 text-text-light" />
              ) : (
                <ChevronDownIcon className="h-6 w-6 text-text-light" />
              )}
            </button>

            {expandedCategories.includes(categoryIndex) && (
              <div className="px-4 py-6 space-y-6">
                {category.items.map((faq, index) => (
                  <div
                    key={index}
                    className="border-b border-border pb-6 last:border-b-0 last:pb-0"
                  >
                    <h3 className="text-lg font-semibold text-foreground mb-3">
                      {faq.question}
                    </h3>
                    <div className="text-text-light">{faq.answer}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="mt-12 p-6 bg-secondary rounded-lg">
        <h2 className="text-xl font-semibold text-foreground mb-4">
          Still have questions?
        </h2>
        <p className="text-text-light mb-4">
          Can't find the answer you're looking for? Our customer support team is
          here to help.
        </p>
        <div className="flex flex-col sm:flex-row gap-4">
          <a
            href="https://wa.me/2348125554459"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block bg-[#25D366] text-white py-2 px-4 rounded-md hover:bg-[#128C7E] focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
          >
            Contact on WhatsApp
          </a>
          <a
            href="mailto:support@sellitoffnow.com"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block bg-[#16a34a] text-white py-2 px-4 rounded-md hover:bg-[#166534] focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
          >
            Email Support
          </a>
        </div>
      </div>
    </div>
  );
}
